import React, { useEffect, useState, useContext, useCallback } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import ProfileLeftSidebar from "./ProfileLeftSidebar";
import toast from "react-hot-toast";
import { Formik, Form } from "formik";
import axios from "axios";
import { TextField, Button, Box } from '@mui/material';
import { AuthContext } from "../../helpers/AuthContext";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import FireplaceIcon from "@mui/icons-material/Fireplace";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import CakeIcon from '@mui/icons-material/Cake';
import SalesBudgetProfilePanel from "./SalesBudgetProfilePanel";
import ProfileAutocomplete from "./ProfileAutocomplete";


import { ROLE_OPTIONS } from '../../utils/roleOptions'


function Profile() {
  let { id } = useParams();
  let history = useHistory();
  const { authState } = useContext(AuthContext);
  const [emailAddress, setEmailAddress] = useState(authState.email);
  const [firstName, setFirstName] = useState(authState.first_name);
  const [lastName, setLastName] = useState(authState.last_name);
  const [phoneNumber, setPhoneNumber] = useState(authState.phone_number);
  const [memberSince, setMemberSince] = useState("");
  const [avatarPreview, setAvatarPreview] = useState("/elite-core-placeholder-avatar.png");
  const [avatarField, setAvatarField] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [attachedUsers, setAttachedUsers] = useState([]);
  const [availableUsers, setAvailableUsers]     = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [recommendedUsersRaw, setRecommendedUsersRaw] = useState([]);  // [{id,role},…]
  const [purchasingReps, setPurchasingReps]      = useState([]);        // full user objects
  const [orderReps, setOrderReps]               = useState([]);        // array of full user objs
  const [birthday, setBirthday] = useState('');  // YYYY-MM-DD
  const [proofProviderSummary, setProofProviderSummary] = useState(null);
  const [binatedCredentials, setBinatedCredentials] = useState(null);
  const [binatedForm, setBinatedForm] = useState({ apiKey: "", webhookSecret: "" });
  const [binatedLoading, setBinatedLoading] = useState(false);
  const [binatedSaving, setBinatedSaving] = useState(false);
  const [binatedRemoving, setBinatedRemoving] = useState(false);
  const [viewedUserRole, setViewedUserRole] = useState(authState.role || "");

  const normalizedRole = String(authState.role || "").replace(/[_\s]+/g, '-');
  const normalizedViewedRole = String(viewedUserRole || authState.role || "").replace(/[_\s]+/g, '-');
  const currentRoleLabel =
    ROLE_OPTIONS.find(opt => opt.value === normalizedViewedRole)?.label
    || viewedUserRole
    || authState.role;
  const canManageSalesBudgets = ["administrator", "admin", "finance", "finance-rep", "manager", "management", "super-administrator"].includes(normalizedRole.toLowerCase());
  // Stable hook dependency; read the current token at request time.
  const authHeaders = useCallback(() => ({
    headers: {
      accessToken: localStorage.getItem("accessToken"),
    },
  }), []);
  const showBinatedApiAccess =
    proofProviderSummary?.enabled === true &&
    String(proofProviderSummary?.provider || "").toLowerCase() === "binated";
    const roleWidgets = {
      "Order Rep": {
        title: "Sales configuration",
        icon: <FireplaceIcon />,
        description: "Customize your personalized sales settings.",
        link: "#",
        buttonText: "Coming soon"
      },
      "Client Rep": {
        title: "Sales configuration",
        icon: <FireplaceIcon />,
        description: "Customize your personalized sales settings.",
        link: "#",
        buttonText: "Coming soon"
      },
      /*{"Administrator": {
        title: "Admin",
        icon: <CreditCardIcon />,
        description: "Manage custom administartive settings.",
        link: `/profile/${id}/payment-methods`,
        buttonText: "Manage admin settings"
      },*/
      "Analytics Rep": {
        title: "Analytics Preferences",
        icon: <SupervisorAccountIcon />,
        description: "Set up your analytics preferences and dashboards.",
        link: "#",
        buttonText: "Coming soon"
      },
      "Design Rep": {
        title: "Design Toolkit",
        icon: <AddCircleIcon />,
        description: "Customize your default templates and design tools.",
        link: "#",
        buttonText: "Coming soon"
      },
      "Production Rep": {
        title: "Production Setup",
        icon: <AddCircleIcon />,
        description: "Configure production scheduling and workflows.",
        link: "#",
        buttonText: "Coming soon"
      },
      "Shipping Rep": {
        title: "Shipping Settings",
        icon: <SupervisorAccountIcon />,
        description: "Manage shipping carriers and tracking visibility.",
        link: "#",
        buttonText: "Coming soon"
      },
      "Web Store Manager": {
        title: "Store Management",
        icon: <AddCircleIcon />,
        description: "Manage default settings for your web stores.",
        link: "#",
        buttonText: "Coming soon"
      }
    };
    

  // Helper function to determine the correct image source.
  const getImageSrc = (imgSrc) => {
    if (typeof imgSrc === "string" && imgSrc.startsWith("uploads")) {
      return `${process.env.REACT_APP_NODEJS_SERVER_URL}/${imgSrc}`;
    }
    return imgSrc;
  };

// Replace purchasing preferences while preserving the other profile preferences.
const savePurchasingReps = () => {
  const newRaw = recommendedUsersRaw
    .filter(e => e.role !== 'purchasing-rep');
  
  // overwrite the purchasing-rep portion
  purchasingReps.forEach(user => {
    newRaw.push({ id: user.id, role: 'purchasing-rep' });
  });
  
  axios.put(`${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/update-recommended-users/${id}`, { recommendedUsers: newRaw })
    .then(() => {
      setRecommendedUsersRaw(newRaw);
      toast('Purchasing Reps saved!');
    })
    .catch(() => toast.error('Failed to save Purchasing Reps'));
};

// Save only the Order Reps
const saveOrderReps = () => {
  // Preserve every purchasing rep when saving order reps.
  const newRaw = recommendedUsersRaw
    .filter(e => e.role !== 'order-rep');
  
  // overwrite the order-reps portion
  orderReps.forEach(u => {
    newRaw.push({ id: u.id, role: 'order-rep' });
  });
  
  axios.put(`${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/update-recommended-users/${id}`, { recommendedUsers: newRaw })
    .then(() => {
      setRecommendedUsersRaw(newRaw);
      toast('Order Reps saved!');
    })
    .catch(() => toast.error('Failed to save Order Reps'));
};


  useEffect(() => {
    if (!localStorage.getItem("accessToken")) {
      history.push("/login");
    } else {
      axios
        .get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/basicinfo/${id}`)
        .then((response) => {
          // --- Update to parse profilePicture as an array ---
          if (response.data.profilePicture != null) {
            let profilePicData = response.data.profilePicture;
            // If the API returns a JSON string, parse it.
            if (typeof profilePicData === "string") {
              try {
                profilePicData = JSON.parse(profilePicData);
              } catch (e) {
                console.error("Error parsing profilePicture JSON:", e);
              }
            }
            if (Array.isArray(profilePicData) && profilePicData.length > 0) {
              // Find the image that is marked selected, or just take the first image.
              const selectedImage = profilePicData.find((img) => img.selected) || profilePicData[0];
              setAvatarPreview(selectedImage.url);
            } else {
              setAvatarPreview(profilePicData);
            }
          }
          setEmailAddress(response.data.email);
          setPhoneNumber(response.data.phone_number);
          setFirstName(response.data.first_name);
          setLastName(response.data.last_name);
          setAttachedUsers(response.data.recommendedUsers || []);
          setBirthday(response.data.birthday || '');
          setRecommendedUsersRaw(response.data.recommendedUsers || []);
          setViewedUserRole(response.data.role || "");
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching basic info:", err);
          setLoading(false);
        });
    }
  }, [authState.id, history, id]);

  useEffect(() => {
    const loadProofProviderSummary = async () => {
      if (!authState?.instanceId) {
        setProofProviderSummary(null);
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/${authState.instanceId}/proof-provider-summary`,
          authHeaders()
        );
        setProofProviderSummary(response.data?.proofProvider || null);
      } catch (error) {
        console.error("Error loading proof provider summary:", error);
        setProofProviderSummary(null);
      }
    };

    loadProofProviderSummary();
  }, [authState?.instanceId, authHeaders]);

  useEffect(() => {
    const loadBinatedCredentials = async () => {
      if (!showBinatedApiAccess) {
        setBinatedCredentials(null);
        return;
      }

      setBinatedLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/users/${id}/api-credentials`,
          authHeaders()
        );
        setBinatedCredentials(response.data?.binated || null);
      } catch (error) {
        console.error("Error loading Binated API credentials:", error);
        setBinatedCredentials(null);
      } finally {
        setBinatedLoading(false);
      }
    };

    loadBinatedCredentials();
  }, [id, showBinatedApiAccess, authHeaders]);

  const saveBirthday = () => {
    axios.put(`${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/update-birthday/${id}`, { birthday })
      .then(() => toast('Birthday saved!'))
      .catch(() => toast.error('Failed to save birthday'));
  };  

  const saveBinatedCredentials = async () => {
    setBinatedSaving(true);
    try {
      const payload = {};
      if (binatedForm.apiKey.trim()) payload.apiKey = binatedForm.apiKey.trim();
      if (binatedForm.webhookSecret.trim()) payload.webhookSecret = binatedForm.webhookSecret.trim();

      const response = await axios.put(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/users/${id}/api-credentials/binated`,
        payload,
        authHeaders()
      );
      setBinatedCredentials(response.data?.binated || null);
      setBinatedForm({ apiKey: "", webhookSecret: "" });
      toast("Binated API credentials saved.");
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to save Binated API credentials.");
    } finally {
      setBinatedSaving(false);
    }
  };

  const removeBinatedCredentials = async () => {
    setBinatedRemoving(true);
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/users/${id}/api-credentials/binated`,
        authHeaders()
      );
      setBinatedCredentials(response.data?.binated || null);
      setBinatedForm({ apiKey: "", webhookSecret: "" });
      toast("Binated API credentials removed.");
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to remove Binated API credentials.");
    } finally {
      setBinatedRemoving(false);
    }
  };

  // ── Fetch everyone in the same instance ──
  useEffect(() => {
    const instanceId = authState.instanceId;
    if (!instanceId) return;

    axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/byinstance/${instanceId}`)
      .then(res => {
        setAvailableUsers(res.data.listOfUsers || []);
      })
      .catch(err => {
        console.error("Error loading team members:", err);
        toast.error("Could not load available users");
      });
  }, [authState.instanceId]);


  useEffect(() => {
    const purchasingUsers = recommendedUsersRaw
      .filter(e => e.role === 'purchasing-rep')
      .map(e => availableUsers.find(u => Number(u.id) === Number(e.id)))
      .filter(Boolean);
    setPurchasingReps(purchasingUsers);
  
    // collect all order-rep entries
    const orderEntries = recommendedUsersRaw.filter(e => e.role === 'order-rep');
    const orderUsers   = orderEntries
      .map(e => availableUsers.find(u => u.id === e.id))
      .filter(Boolean);
    setOrderReps(orderUsers);
  
  }, [availableUsers, recommendedUsersRaw]);

  const compressImageFile = async (file, maxSize = 1200, quality = 0.82) => {
    const imageBitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxSize / Math.max(imageBitmap.width, imageBitmap.height));
    const w = Math.max(1, Math.round(imageBitmap.width * scale));
    const h = Math.max(1, Math.round(imageBitmap.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(imageBitmap, 0, 0, w, h);

    const blob = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", quality)
    );

    return new File([blob], `${(file.name || "profile").replace(/\.[^.]+$/, "")}-optimized.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!avatarField) {
      toast("Please choose an image first.");
      return;
    }

    const optimized = await compressImageFile(avatarField);
    if (optimized.size > 2 * 1024 * 1024) {
      toast("Image is still too large after optimization (max 2MB). Please choose a smaller photo.");
      return;
    }

    const data = new FormData();
    data.append("profilePicture", optimized);
    data.append("userId", id);
    axios
      .post(`${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/update-profile-picture`, data, {
        headers: {
          accessToken: localStorage.getItem("accessToken"),
        },
      })
      .then((response) => {
        // Optionally, update the preview from response data:
        if (response.data) {
          // Use the returned array to set the preview.
          const updatedPicData = Array.isArray(response.data) ? response.data : [];
          if (updatedPicData.length > 0) {
            const selected = updatedPicData.find((img) => img.selected) || updatedPicData[0];
            setAvatarPreview(selected.url);
          }
        }
        toast("Your profile picture has been successfully updated.");
      })
      .catch((err) => {
        console.error("Upload failed:", err);
        toast("Failed to update your profile picture.");
      });
  };

  function messagePopup() {
    setShowPopup(true);
  }
  function closePopup() {
    setShowPopup(false);
  }

  return (
    <div className="ec-app-instances">
      {showPopup ? (
        <div className="popup update-logo">
          <div className="popup_inner">
            <h1>Upload a new profile picture</h1>
            <Formik>
              <Form>
                <div className="popup-content">
                  <div className="lead-selection-section">
                    <label htmlFor="profilePicture">
                      Upload a new profile picture that may be shown on your live chats based on your
                      preferences.
                    </label>
                    <div className="brand-logo-preview-container">
                      {/* Use the helper function for the popup preview */}
                      <img
                        className="brand-logo-preview"
                        src={getImageSrc(avatarPreview)}
                        alt="Profile preview"
                      />
                    </div>
                  </div>
                  <input
                    name="profilePicture"
                    accept="image/jpeg,image/png,image/webp"
                    id="profilePicture"
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
                        toast("Please upload a JPEG, PNG, or WebP image.");
                        return;
                      }
                      if (file.size > 8 * 1024 * 1024) {
                        toast("Please choose an image smaller than 8MB before optimization.");
                        return;
                      }

                      const fileReader = new FileReader();
                      fileReader.onload = () => {
                        if (fileReader.readyState === 2) {
                          const img = new Image();
                          img.onload = () => {
                            if (img.width > 6000 || img.height > 6000) {
                              toast("Image dimensions are too large. Please choose an image up to 6000x6000.");
                              return;
                            }
                            setAvatarField(file);
                            setAvatarPreview(fileReader.result);
                          };
                          img.src = fileReader.result;
                        }
                      };
                      fileReader.readAsDataURL(file);
                    }}
                  />
                  <div className="popup-button-group">
                    <button type="submit" onClick={onSubmit} className="popup-button popup-button-save">
                      Upload profile picture
                    </button>
                    <button className="popup-button" onClick={closePopup}>
                      Close popup
                    </button>
                  </div>
                </div>
              </Form>
            </Formik>
          </div>
        </div>
      ) : (
        ""
      )}
      <ProfileLeftSidebar />
      <div className="rightSide">
        {!loading ? (
          <div className="options-container fade-in-effect-usability">
            <div className="options-inner-container">
              <div className="core-user-container">
                <div className="core-user-credentials">
                  <div className="brand-logo">
                    {/* Use the helper function on the main profile display */}
                    <img
                      className="brand-logo-preview"
                      src={getImageSrc(avatarPreview)}
                      alt="Profile"
                    />
                  </div>
                  <div className="brand-name">
                    <h1>{firstName + " " + lastName}</h1>
                    <p>{emailAddress}</p>
                    <span><SupervisorAccountIcon/> {currentRoleLabel}</span>
                  </div>
                </div>
                <div className="core-user-actions">
                  <Link
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      messagePopup();
                    }}
                    className="change-brand-logo"
                  >
                    <AddCircleIcon /> Upload profile picture
                  </Link>
                </div>
              </div>

              {roleWidgets[currentRoleLabel] && (
                <div className="profile-db-widget-box">
                  <div className="inner-widget-box">
                    <div className="widget-heading">
                      <h3>{roleWidgets[currentRoleLabel].title}</h3>
                      <div className="widget-icon">
                        {roleWidgets[currentRoleLabel].icon}
                      </div>
                    </div>
                    <div className="widget-details">
                      <span className="archive-statistics-number"></span>
                      <span className="archive-statistics-label">
                        {roleWidgets[currentRoleLabel].description}
                      </span>
                      <Link
                        to={roleWidgets[currentRoleLabel].link}
                        className="archive-statistics-button"
                      >
                        {roleWidgets[currentRoleLabel].buttonText}
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              <SalesBudgetProfilePanel
                authState={authState}
                targetUserId={id}
                targetUserRole={viewedUserRole}
                canManage={canManageSalesBudgets}
              />

{/* ── Preferred Purchasing Reps ── */}
<div className="profile-db-widget-box">
  <div className="inner-widget-box">
    <div className="widget-heading">
      <h3>Preferred purchasing reps</h3>
      <div className="widget-icon">
        <SupervisorAccountIcon />
      </div>
    </div>
    <div className="widget-details">
      <span className="archive-statistics-label">
        Select your preferred purchasing reps. They will automatically be assigned to all orders you create,
        and will be notified into the process when any order reaches the purchase order stage.
      </span>
      <ProfileAutocomplete
        multiple
        className="profile-db-select-box"
        options={availableUsers.filter(u => u.role === 'purchasing-rep')}
        getOptionLabel={u => `${u.first_name} ${u.last_name}`}
        isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
        value={purchasingReps}
        onChange={(e, v) => setPurchasingReps(v)}
        renderInput={params => (
          <TextField {...params} label="Select Purchasing Reps" />
        )}
      />
      <button
        type="button"
        className="archive-statistics-button"
        onClick={savePurchasingReps}
      >
        Save
      </button>
    </div>
  </div>
</div>

{/* ── Preferred Order Reps ── */}
<div className="profile-db-widget-box">
  <div className="inner-widget-box">
    <div className="widget-heading">
      <h3>Preferred order reps</h3>
      <div className="widget-icon">
        <SupervisorAccountIcon />
      </div>
    </div>
    <div className="widget-details">
      <span className="archive-statistics-label">
        Select your order reps. In real-world workflows, order reps and client reps work together -
        these users will be added automatically to any orders you create.
      </span>
      <ProfileAutocomplete
        className="profile-db-select-box"
        multiple
        options={availableUsers.filter(u => u.role === 'order-rep')}
        getOptionLabel={u => `${u.first_name} ${u.last_name}`}
        value={orderReps}
        onChange={(e, v) => setOrderReps(v)}
        renderInput={params => (
          <TextField {...params} label="Select Order Reps" />
        )}
      />
      <button
        type="button"
        className="archive-statistics-button"
        onClick={saveOrderReps}
      >
        Save
      </button>
    </div>
  </div>
</div>

<div className="profile-db-widget-box">
  <div className="inner-widget-box">
    <div className="widget-heading">
      <h3>Birthday</h3>
      <div className="widget-icon">
        <CakeIcon />
      </div>
    </div>
    <div className="widget-details">
      <span className="archive-statistics-label">
        Add your birthday so we can celebrate your special day with you!
      </span>
      <TextField
        type="date"
        label="Select your birth date"
        value={birthday}
        onChange={e => setBirthday(e.target.value)}
        InputLabelProps={{ shrink: true }}
        className="profile-db-select-box"
      />
      <Button
        className="archive-statistics-button"
        variant="contained"
        sx={{ mt: 2 }}
        onClick={saveBirthday}
      >
        Save Birthday
      </Button>
    </div>
  </div>
</div>

              {showBinatedApiAccess ? (
                <div className="profile-db-widget-box">
                  <div className="inner-widget-box">
                    <div className="widget-heading">
                      <h3>API Access</h3>
                      <div className="widget-icon">
                        <VpnKeyIcon />
                      </div>
                    </div>
                    <div className="widget-details">
                      <span className="archive-statistics-label">
                        Use your API key to authenticate requests from your own applications. Keep this key private and do not share it publicly.
                      </span>
                      <div style={{ marginTop: "16px" }}>
                        <h4 style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
                          Binated API Credentials
                          {!binatedLoading && binatedCredentials ? (
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.75,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: "999px",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                lineHeight: 1.3,
                                bgcolor: binatedCredentials.enabledForCurrentInstance === true && binatedCredentials.isComplete === true
                                  ? "success.light"
                                  : "action.hover",
                                color: binatedCredentials.enabledForCurrentInstance === true && binatedCredentials.isComplete === true
                                  ? "common.white"
                                  : "text.secondary",
                                border: "1px solid",
                                borderColor: binatedCredentials.enabledForCurrentInstance === true && binatedCredentials.isComplete === true
                                  ? "success.main"
                                  : "divider",
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  width: 7,
                                  height: 7,
                                  borderRadius: "50%",
                                  bgcolor: binatedCredentials.enabledForCurrentInstance === true && binatedCredentials.isComplete === true
                                    ? "success.main"
                                    : "text.disabled",
                                  flexShrink: 0,
                                }}
                              />
                              {binatedCredentials.enabledForCurrentInstance === true && binatedCredentials.isComplete === true
                                ? "Your Binated account is connected."
                                : "Your Binated account is not connected."}
                            </Box>
                          ) : null}
                        </h4>
                        <span className="archive-statistics-label">
                          This appears because Binated is enabled as the proof provider for the current instance.
                        </span>
                        <span className="archive-statistics-label" style={{ marginTop: "8px", display: "block" }}>
                          User-level Binated credentials will be used when a user API key is saved. You can also save a user-level webhook secret for callbacks. Otherwise Elite Core uses the instance-level Binated connection.
                        </span>
                      </div>
                      {binatedLoading ? (
                        <span className="archive-statistics-label" style={{ marginTop: "12px" }}>
                          Loading saved credential status...
                        </span>
                      ) : (
                        <>
                          <TextField
                            type="password"
                            label="Binated API Key"
                            value={binatedForm.apiKey}
                            onChange={(e) => setBinatedForm((current) => ({ ...current, apiKey: e.target.value }))}
                            placeholder={
                              binatedCredentials?.hasApiKey
                                ? `Saved (last 4: ${binatedCredentials.apiKeyLastFour || "...."})`
                                : "Enter a new Binated API key"
                            }
                            helperText={
                              binatedCredentials?.hasApiKey
                                ? `Saved status: last 4 ${binatedCredentials.apiKeyLastFour || "unknown"}`
                                : "No user-level API key saved."
                            }
                            sx={{ width: "100%", mt: 2 }}
                          />
                          <TextField
                            type="password"
                            label="Binated Webhook Secret"
                            value={binatedForm.webhookSecret}
                            onChange={(e) => setBinatedForm((current) => ({ ...current, webhookSecret: e.target.value }))}
                            placeholder={
                              binatedCredentials?.hasWebhookSecret
                                ? `Saved (last 4: ${binatedCredentials.webhookSecretLastFour || "...."})`
                                : "Enter a new Binated webhook secret"
                            }
                            helperText={
                              binatedCredentials?.hasWebhookSecret
                                ? `Saved status: last 4 ${binatedCredentials.webhookSecretLastFour || "unknown"}`
                                : "Optional: save your user-level Binated webhook secret."
                            }
                            sx={{ width: "100%", mt: 2 }}
                          />
                          {binatedCredentials?.updatedAt ? (
                            <span className="archive-statistics-label" style={{ marginTop: "12px" }}>
                              Last updated: {new Date(binatedCredentials.updatedAt).toLocaleString()}
                            </span>
                          ) : null}
                          <Box sx={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap", mt: 2 }}>
                            <Button
                              variant="contained"
                              onClick={saveBinatedCredentials}
                              disabled={binatedSaving || (!binatedForm.apiKey.trim() && !binatedForm.webhookSecret.trim())}
                              sx={{ minHeight: 40 }}
                            >
                              {binatedSaving ? "Saving..." : "Save Credentials"}
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={removeBinatedCredentials}
                              disabled={binatedRemoving || (!binatedCredentials?.hasApiKey && !binatedCredentials?.hasWebhookSecret)}
                              sx={{ minHeight: 40 }}
                            >
                              {binatedRemoving ? "Removing..." : "Remove Credentials"}
                            </Button>
                          </Box>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}

              {authState.email === emailAddress && (
                <div className="profile-db-widget-box">
                  <div className="inner-widget-box">
                    <div className="widget-heading">
                      <h3>Change password</h3>
                      <div className="widget-icon">
                        <VpnKeyIcon />
                      </div>
                    </div>
                    <div className="widget-details">
                      <span className="archive-statistics-number"></span>
                      <span className="archive-statistics-label">
                        Change the password associated with your account.
                      </span>
                      <Link to={`/profile/${id}/change-password`} className="archive-statistics-button">
                        Get started
                      </Link>
                    </div>
                  </div>
                </div>
              )}
              { Number(authState.id) === Number(id) && ["administrator", "super-administrator"].includes(normalizedRole.toLowerCase()) ?
              <div className="profile-db-widget-box">
                <div className="inner-widget-box">
                  <div className="widget-heading">
                    <h3>Payment methods</h3>
                    <div className="widget-icon">
                      <CreditCardIcon />
                    </div>
                  </div>
                  <div className="widget-details">
                    <span className="archive-statistics-number"></span>
                    <span className="archive-statistics-label">
                      Manage all of your payment methods utilized for billing.
                    </span>
                    <Link to={`/profile/${id}/payment-methods`} className="archive-statistics-button">
                      View payment methods
                    </Link>
                  </div>
                </div>
              </div>
              : "" }
            </div>
          </div>
        ) : (
          ""
        )}
      </div>
    </div>
  );
}

export default Profile;
